import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Clock, Users, Folder, TrendingUp, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { Button } from '../components/ui/button';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#f59e0b', '#ec4899', '#14b8a6', '#6366f1'];

const PERIOD_OPTIONS = [
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'All Time', value: 'all' },
  { label: 'Custom', value: 'custom' },
];

export default function HoursAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, [period, startDate, endDate]);

  const getDateRange = () => {
    const now = new Date();
    if (period === 'week') {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      return { start: start.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
    }
    if (period === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: start.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
    }
    if (period === 'custom' && startDate && endDate) {
      return { start: startDate, end: endDate };
    }
    return {};
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      const params = new URLSearchParams();
      if (range.start) params.append('start_date', range.start);
      if (range.end) params.append('end_date', range.end);
      const res = await axios.get(`${API}/analytics/hours?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 pt-20 pb-28">
        <div className="max-w-5xl mx-auto animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl" />)}
          </div>
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-4 pt-20 pb-28" data-testid="hours-analytics-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-900">Hours Analytics</h1>
          </div>
        </div>

        {/* Period Filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                period === opt.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`period-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Custom Date Range */}
        {period === 'custom' && (
          <div className="flex gap-2 mb-4 items-center">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              data-testid="custom-start"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              data-testid="custom-end"
            />
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <Clock className="w-5 h-5 text-orange-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.summary.total_hours}</p>
            <p className="text-xs text-gray-500">Total Hours</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <Users className="w-5 h-5 text-blue-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.summary.total_employees}</p>
            <p className="text-xs text-gray-500">Employees</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <Folder className="w-5 h-5 text-green-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.summary.total_projects}</p>
            <p className="text-xs text-gray-500">Projects</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <TrendingUp className="w-5 h-5 text-purple-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.summary.avg_hours_per_entry}</p>
            <p className="text-xs text-gray-500">Avg Hours/Entry</p>
          </div>
        </div>

        {/* Hours by Employee - Bar Chart */}
        {data.hours_by_employee.length > 0 && (
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
            <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" /> Hours by Employee
            </h2>
            <div style={{ width: '100%', height: Math.max(200, data.hours_by_employee.length * 35) }}>
              <ResponsiveContainer>
                <BarChart data={data.hours_by_employee} layout="vertical" margin={{ left: 80, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
                  <Tooltip formatter={(val) => [`${val} hrs`, 'Hours']} />
                  <Bar dataKey="hours" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Hours by Project - Pie Chart */}
        {data.hours_by_project.length > 0 && (
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
            <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <Folder className="w-4 h-4 text-green-500" /> Hours by Project
            </h2>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div style={{ width: '100%', maxWidth: '300px', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={data.hours_by_project.slice(0, 10)}
                      dataKey="hours"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name.substring(0, 12)}${name.length > 12 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                      fontSize={10}
                    >
                      {data.hours_by_project.slice(0, 10).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => [`${val} hrs`, 'Hours']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1">
                {data.hours_by_project.slice(0, 10).map((p, i) => (
                  <div key={p.name} className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-gray-700 truncate flex-1">{p.name}</span>
                    <span className="text-gray-500 font-medium">{p.hours} hrs</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Daily Trend - Line Chart */}
        {data.daily_trend.length > 0 && (
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
            <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-500" /> Daily Hours Trend
            </h2>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={data.daily_trend} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(d) => { const dt = new Date(d + 'T12:00'); return `${dt.getMonth()+1}/${dt.getDate()}`; }}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(d) => new Date(d + 'T12:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
                    formatter={(val) => [`${val} hrs`, 'Total Hours']}
                  />
                  <Line type="monotone" dataKey="hours" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Overtime Tracking */}
        {data.overtime.length > 0 && (
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
            <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" /> Overtime (40+ hrs/week)
            </h2>
            <div className="space-y-2">
              {data.overtime.map((emp) => (
                <div key={emp.name} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{emp.name}</p>
                    <p className="text-xs text-gray-500">Total: {emp.total_hours} hrs</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-600">+{emp.overtime_hours}</p>
                    <p className="text-xs text-red-500">overtime hrs</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No data state */}
        {data.hours_by_employee.length === 0 && (
          <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm text-center">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No time entries found for this period</p>
          </div>
        )}
      </div>
    </div>
  );
}
